import { defineStore } from "pinia";
import axios from "axios";
import { Preferences } from "@capacitor/preferences";
import router from "@/router/index";

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null,
    token: null,
    refreshToken: null,
    mvCoins: 0,
    direcciones: [],
    isAuthenticated: false,
    isSessionReady: false,
    interceptorSetupDone: false,
  }),



  actions: {
    // Guardar usuario en Pinia y localStorage
    setUser(userData) {
      this.user = userData;
      localStorage.setItem("user", JSON.stringify(userData));
    },

    // Obtener MV Coins
    async obtenerMVCoins() {
      if (!this.user || !this.user.id) return;

      try {
        const response = await axios.get(
          `https://mvexpress.mx/api/get-mvcoins/${this.user.id}`,
          { headers: { Authorization: `Bearer ${this.user.token}` } }
        );
        this.user = { ...this.user, ...response.data };
        this.mvCoins = parseFloat(response.data.mvCoins || 0);
        localStorage.setItem("user", JSON.stringify(this.user));
      } catch (error) {
        console.error("Error al obtener MV Coins:", error);
      }
    },

    // Obtener direcciones
    async obtenerDirecciones() {
      if (!this.user || !this.user.cliente) {
        console.warn("Usuario no logueado, redirigiendo a login...");
        return null;
      }

      const cacheKey = `direcciones_${this.user.cliente}`;
      const cacheTimeKey = `${cacheKey}_timestamp`;
      const now = Date.now();
      const oneWeek = 7 * 24 * 60 * 60 * 1000;

      const cachedData = localStorage.getItem(cacheKey);
      const cachedTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cachedTime && now - cachedTime < oneWeek) {
        this.direcciones = JSON.parse(cachedData);
        return this.direcciones;
      }

      try {
        const response = await axios.get(
          `https://mvexpress.mx/api/find-direccion/${this.user.cliente}`,
          { headers: { Authorization: `Bearer ${this.user.token}` } }
        );

        this.direcciones = response.data.filter(
          (dir, index, self) =>
            index === self.findIndex((d) => d.CTEDIR_CODIGO_K === dir.CTEDIR_CODIGO_K)
        );

        const jsonData = JSON.stringify(this.direcciones);
        if (jsonData.length < 4 * 1024 * 1024) {
          localStorage.setItem(cacheKey, jsonData);
          localStorage.setItem(cacheTimeKey, now);
        }

        return this.direcciones;
      } catch (error) {
        console.error("Error al obtener direcciones:", error);
        return null;
      }
    },
    async obtenerNuevaData() {
      try {
        const user = this.user;
        if (!user || !user.id) return;

        const response = await axios.get(`https://mvexpress.mx/api/get-mvcoins/${user.id}`);
        this.user = response.data; // o actualiza userStore.user
      } catch (err) {
        console.error(err);
      }
    },
    async login(email, password) {
      const { data } = await axios.post("https://mvexpress.mx/api/auth", {
        email,
        password,
      });

      this.token = data.token;
      this.refreshToken = data.refreshToken;
      this.isAuthenticated = true;


      this.user = {
        id: data.id,
        cliente: data.cliente,
        email: data.email,
        usuario: data.usuario,
        razon: data.razon,
        rfc: data.rfc,
        telefono: data.telefono,
        mvCoins: data.mvCoins,
        forma_pago: data.forma_pago,
        uso_cfdi: data.uso_cfdi,
        denominacion_comercial: data.denominacion_comercial,
        ruta_preventa: data.ruta_preventa,
        ruta_reparto: data.ruta_reparto,
        politica_privacidad_aceptada: data.politica_privacidad_aceptada,
        fecha_politica_privacidad: data.fecha_politica_privacidad,
        grupo_precio: data.grupo_precio ?? null,
        descuento_porcentaje: data.descuento_porcentaje ?? 0,
        tiene_descuento: data.tiene_descuento ?? false,
      };

      this.direcciones = data.direcciones || [];

      await Preferences.set({
        key: "auth_session",
        value: JSON.stringify({
          token: data.token,
          refreshToken: data.refreshToken,
          user: this.user,
          direcciones: this.direcciones,
        }),
      });

      axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      this.setupAxiosInterceptors(); // 🛡️ Activar interceptor al loguearse
    },


    async initSession() {
      if (this.isSessionReady) return;

      const { value } = await Preferences.get({ key: "auth_session" });

      if (!value) {
        this.isSessionReady = true;
        return;
      }

      const session = JSON.parse(value);

      if (session && session.token) {
        // ⏰ Revisar expiración del token al iniciar sesión
        try {
          const payload = JSON.parse(atob(session.token.split('.')[1]));
          const expDate = payload.exp * 1000;

          if (Date.now() > expDate) {
            this.refreshToken = session.refreshToken;
            try {
              const newToken = await this.refreshAccessToken();
              session.token = newToken;
            } catch (e) {
              await this.logout();
              this.isSessionReady = true;
              router.push('/login');
              return;
            }
          }
        } catch (e) { /* token malformado, se continúa */ }

        this.token = session.token;
        this.refreshToken = session.refreshToken;
        this.user = session.user;
        this.direcciones = session.direcciones || [];
        this.isAuthenticated = true;
        axios.defaults.headers.common.Authorization = `Bearer ${session.token}`;
        this.setupAxiosInterceptors();
        // this.sincronizarVersionReglas(); // desactivado — endpoint aún no existe en backend
      } else {
        this.isAuthenticated = false;
      }

      this.isSessionReady = true;
    },

    // Limpia el caché de productos si el admin cambió reglas de descuento
    async sincronizarVersionReglas() {
      try {
        const { data } = await axios.get(`${import.meta.env.VITE_API_URL || 'https://mvexpress.mx/api'}/grupo-precio/cache/version`);
        const versionRemota = String(data.version);
        const versionLocal = localStorage.getItem('rules_version');

        if (versionLocal !== versionRemota) {
          // Borrar sólo claves de caché de productos, no la sesión ni el carrito
          const keysToDelete = Object.keys(localStorage).filter(k =>
            k.startsWith('products_') ||
            k.startsWith('top10_') ||
            k.startsWith('energizate100') ||
            k.startsWith('ofertados_')
          );
          keysToDelete.forEach(k => localStorage.removeItem(k));
          localStorage.setItem('rules_version', versionRemota);
        }
      } catch {
        // Si el backend no responde, continúa normal
      }
    },

    async updateToken(newToken) {
      this.token = newToken;
      // Actualizar header global de axios por si se usa fuera de api.js
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      // Actualizar persistencia
      const { value } = await Preferences.get({ key: "auth_session" });
      if (value) {
        const session = JSON.parse(value);
        session.token = newToken;
        await Preferences.set({ key: "auth_session", value: JSON.stringify(session) });
      }
    },

    // 🔄 ACCIÓN: Llamar a tu endpoint de backend para renovar token
    async refreshAccessToken() {
      try {
        if (!this.refreshToken) throw new Error("No hay refresh token disponible");

        const { data } = await axios.post("https://mvexpress.mx/api/refresh-token", {
          refreshToken: this.refreshToken
        }, {
          headers: { Authorization: '' }
        });

        await this.updateToken(data.token);
        return data.token;
      } catch (error) {
        await this.logout();
        router.push('/login');
        throw error;
      }
    },

    // 🛡️ INTERCEPTOR: Captura errores 401 y reintenta automáticamente
    setupAxiosInterceptors() {
      if (this.interceptorSetupDone) return;

      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;
          // Si es error 401 (No autorizado) O 403 (Prohibido) y no lo hemos reintentado aún
          if (error.response && (error.response.status === 401 || error.response.status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const newToken = await this.refreshAccessToken();
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest); // Reintentar petición original
            } catch (err) {
              return Promise.reject(err);
            }
          }
          return Promise.reject(error);
        }
      );
      this.interceptorSetupDone = true;
    },


    // Cerrar sesión
    async logout() {
      // Guardar el carrito antes de borrar todo
      const carritoGuardado = localStorage.getItem("cart");

      // Limpiar store
      this.user = null;
      this.token = null;
      this.refreshToken = null;
      this.direcciones = [];
      this.isAuthenticated = false;
      this.isSessionReady = true;

      // 🔥 FUERZA BRUTA: Borra TODAS las claves de Preferences (auth_session, caches, y tus nuevas keys)
      // Esto asegura que no quede basura de ninguna vista.
      await Preferences.clear();

      localStorage.clear();
      sessionStorage.clear();

      // Restaurar el carrito
      if (carritoGuardado) {
        localStorage.setItem("cart", carritoGuardado);
      }
    },

    testCorruptToken() {
      this.token = "TOKEN_CORRUPTO_TEST_" + Date.now();
    }


  },
});
